import {useState, useCallback} from "react";
import apiClient from "../utils/apiClient";
import {getApiErrorMessage} from "../utils/apiError";
import {snackbar} from "../utils/snackbar";
import {useAuthPermissions} from "./useAuthPermissions";

const METHOD_SCOPE_MAP = {
  get: "read",
  post: "write",
  put: "write",
  patch: "write",
  delete: "delete"
};

const useApiRequest = ({baseUrl = "", resource = "", field = "", autoErrorSnackbar = true}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [response, setResponse] = useState(null);

  const {verifyScope} = useAuthPermissions();

  const makeRequest = useCallback(
    async (method, url, data = null, config = {}) => {
      setLoading(true);
      setError(null);

      // `silentError` es una opcion nuestra: hay que quitarla antes de pasar el config a axios.
      const {silentError = false, ...axiosConfig} = config || {};

      // Registra el error, lo enriquece con el mensaje de usuario y decide si sacar snackbar.
      // El 401 se excluye: apiClient ya intenta el refresh y, si falla, redirige a /acceso,
      // asi que el snackbar se perderia en la navegacion.
      const handleError = (err) => {
        const userMessage = getApiErrorMessage(err);
        err.userMessage = userMessage;
        setError(err);

        if (autoErrorSnackbar && !silentError && err.response?.status !== 401) {
          snackbar.error(userMessage);
        }

        return {
          success: false,
          error: err,
          userMessage,
          data: null,
          method: method.toLowerCase()
        };
      };

      try {
        // 🔐 Verificación de scopes
        const action = METHOD_SCOPE_MAP[method.toLowerCase()];
        const fieldScope = field ? `.${field}` : "";
        const requiredScope = resource && action ? `identity.${resource}${fieldScope}.global.${action}` : null;

        if (requiredScope && !verifyScope(requiredScope)) {
          const forbiddenError = new Error("Forbidden: Scope required - " + requiredScope);
          forbiddenError.code = "FORBIDDEN";

          return handleError(forbiddenError);
        }

        // 🌐 URL completa
        const fullUrl = baseUrl ? `${baseUrl}${url}` : url;

        let axiosResponse;

        switch (method.toLowerCase()) {
          case "get":
            axiosResponse = await apiClient.get(fullUrl, axiosConfig);
            break;
          case "post":
            axiosResponse = await apiClient.post(fullUrl, data, axiosConfig);
            break;
          case "put":
            axiosResponse = await apiClient.put(fullUrl, data, axiosConfig);
            break;
          case "patch":
            axiosResponse = await apiClient.patch(fullUrl, data, axiosConfig);
            break;
          case "delete":
            axiosResponse = await apiClient.delete(fullUrl, axiosConfig);
            break;
          default:
            throw new Error(`Método HTTP no soportado: ${method}`);
        }

        const enhancedResponse = {
          ...axiosResponse,
          method: method.toLowerCase(),
          url: fullUrl,
          timestamp: new Date().toISOString()
        };

        setData(axiosResponse.data);
        setResponse(enhancedResponse);

        return {
          success: true,
          data: axiosResponse.data,
          response: enhancedResponse
        };
      } catch (error) {
        const finalUrl = baseUrl ? `${baseUrl}${url}` : url;
        console.error(`Error en ${method.toUpperCase()} ${finalUrl}:`, error);
        return handleError(error);
      } finally {
        setLoading(false);
      }
    },
    [baseUrl, resource, field, verifyScope, autoErrorSnackbar]
  );

  // CRUD helpers
  const create = useCallback((data, config) => makeRequest("post", "", data, config), [makeRequest]);

  const read = useCallback((id = "", config) => makeRequest("get", id ? `/${id}` : "", null, config), [makeRequest]);

  const update = useCallback((id, data, config) => makeRequest("put", `/${id}`, data, config), [makeRequest]);

  const remove = useCallback((id, config) => makeRequest("delete", `/${id}`, null, config), [makeRequest]);

  const list = useCallback((params, config) => makeRequest("get", "", null, {...config, params}), [makeRequest]);

  const clearError = useCallback(() => setError(null), []);
  const resetData = useCallback(() => setData(null), []);

  return {
    loading,
    error,
    data,
    response,

    makeRequest,
    create,
    read,
    update,
    remove,
    list,

    clearError,
    resetData,
    setData
  };
};

export default useApiRequest;
